import { SCOPES } from './../../../../services/scope/scope-constants';
import { optionalName } from '../../../../validators/name-rules';
import { Component, inject, signal, LOCALE_ID, computed, DestroyRef } from '@angular/core';
import { Router } from '@angular/router';
// import { $localize } from '@angular/localize/init';
import { FilterInput } from '../../../../shared/ui/shared-filters/filter-input/filter-input';
import { SubmitButton } from '../../../../shared/ui/Buttons/submit-button/submit-button';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { SuccessfulNotification } from '../../../../../../src/app/utils/successful-notification/successful-notification';
import { RejectionReasonService } from '../../../../services/rejection-reason/rejection-reason.service';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
export function arabicCharactersValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string;

    if (!value) {
      return null;
    }

    const arabicPattern = /^[\u0600-\u06FF]+(?:[ -][\u0600-\u06FF]+)*$/;

    return arabicPattern.test(value) ? null : { invalidArabicCharacters: true };
  };
}
export function englishCharactersValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string;

    if (!value) {
      return null;
    }

    const englishPattern = /^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/;

    return englishPattern.test(value) ? null : { invalidEnglishCharacters: true };
  };
}
export function noLeadingOrTrailingSpacesValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string;

    if (!value) {
      return null;
    }

    return value !== value.trim() ? { leadingOrTrailingSpaces: true } : null;
  };
}

export function noNumbersValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string;

    if (!value) {
      return null;
    }

    return /\d/.test(value) ? { numbersNotAllowed: true } : null;
  };
}

@Component({
  selector: 'app-create-rejection-reason',
  imports: [FilterInput, SubmitButton, ConfirmationDialog, SuccessfulNotification, ReactiveFormsModule],
  templateUrl: './create-rejection-reason.html',
  styleUrl: './create-rejection-reason.scss',
})
export class CreateRejectionReason {
  onArabicValueChange(value: string | number | null) {
    this.form.controls.reasonAR.setValue(typeof value === 'string' ? value : '');
  }

  onEnglishValueChange(value: string | number | null) {
    this.form.controls.reasonEN.setValue(typeof value === 'string' ? value : '');
  }
  showValidationErrors = signal(false);
  confirmCreateReasonTitle = $localize`:@@confirmCreateReasonTitle:Create New Reason`;
  confirmCreateReasonDescription = $localize`:@@confirmCreateReasonDescription:Are you sure you want to create this reason?`;
  confirmButton = signal($localize`:@@cancellationReasonConfirm:Confirm`);

  Loading = $localize`:@@loading:Loading...`;
  cancelButton = $localize`:@@adminCategoryFormCancelButton:Cancel`;
  reasonENPlaceholder = $localize`:@@rejectionReasonEnglishNamePlaceholder:Enter name in English (optional)`;
  reasonARPlaceholder = $localize`:@@rejectionReasonArabicNamePlaceholder:Enter name in Arabic`;
  reasonArabicLabel = $localize`:@@rejectionReasonArabicLabel:Reason Title (Arabic)`;
  reasonEnglishLabel = $localize`:@@rejectionReasonEnglishLabel:Reason Title (English)`;
  rejectionReasonArabicRequired = $localize`:@@rejectionReasonArabicRequired:Reason Name in Arabic is required`;
  rejectionReasonMinLength = $localize`:@@rejectionReasonMinLength:Reason Name must be at least 3 characters`;
  rejectionReasonMaxLength = $localize`:@@rejectionReasonMaxLength:Reason Name cannot exceed 255 characters`;
  rejectionReasonArabicSpaces = $localize`:@@rejectionReasonArabicSpaces:Arabic Reason title cannot start or end with spaces`;
  rejectionReasonNumbersNotAllowed = $localize`:@@rejectionReasonNumbersNotAllowed:Reason Name cannot contain numbers`;
  rejectionReasonEnglishRequired = $localize`:@@rejectionReasonEnglishRequired:Reason Name in English is required`;
  rejectionReasonEnglishSpaces = $localize`:@@rejectionReasonArabicSpaces:English Reason title cannot start or end with spaces`;
  rejectionReasonArabicCharacters = $localize`:@@rejectionReasonArabicCharacters:Reason Name can contain Arabic letters, spaces, and hyphens only`;
  rejectionReasonEnglishCharacters = $localize`:@@rejectionReasonEnglishCharacters:Reason Name can contain English letters, spaces, hyphens, and apostrophes only`;
  private rejectionReasonService = inject(RejectionReasonService);
  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  isCreating = signal(false);
  readonly SCOPES = SCOPES;

  isArabic = computed(() => this.locale.startsWith('ar'));

  showConfirmation = signal(false);
  showCreateSuccess = signal(false);
  createReasonError = signal('');

  createdReasonAR = signal('');
  createdReasonEN = signal('');

  successTitle = computed(() =>
    this.isArabic()
      ? `تم إنشاء سبب الرفض '${this.createdReasonAR()}' بنجاح`
      : `Rejection reason '${this.createdReasonEN()}' created successfully`,
  );

  form = new FormGroup({
    reasonAR: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(255),
        noLeadingOrTrailingSpacesValidator(),
        noNumbersValidator(),
        arabicCharactersValidator(),
      ],
    }),

    reasonEN: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.minLength(3),
        Validators.maxLength(255),
        noLeadingOrTrailingSpacesValidator(),
        noNumbersValidator(),
        englishCharactersValidator(),
      ],
    }),
  });
  private destroyRef = inject(DestroyRef);
  goBack() {
    this.router.navigate(['/admin/rejection-reasons']);
  }

  onSubmit() {
    this.showValidationErrors.set(true);
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    this.showConfirmation.set(true);
  }
  closeConfirmation() {
    this.showConfirmation.set(false);
    this.createReasonError.set('');
  }

  onConfirmCreate() {
    this.createReasonError.set('');
    this.isCreating.set(true);
    this.confirmButton.set(this.Loading);
    this.createdReasonAR.set(this.form.controls.reasonAR.value);

    this.createdReasonEN.set(this.form.controls.reasonEN.value || this.form.controls.reasonAR.value);

    const dto = {
      titleAr: this.form.controls.reasonAR.value,
      titleEn: optionalName(this.form.controls.reasonEN.value),
    };
    const subscription = this.rejectionReasonService.createRejectionReason(dto).subscribe({
      next: () => {
        this.isCreating.set(false);
        this.confirmButton.set($localize`:@@cancellationReasonConfirm:Confirm`);
        this.showConfirmation.set(false);
        this.showCreateSuccess.set(true);

        setTimeout(() => {
          this.showCreateSuccess.set(false);
          this.goBack();
        }, 3000);
      },

      error: (error) => {
        this.isCreating.set(false);
        this.confirmButton.set($localize`:@@cancellationReasonConfirm:Confirm`);
        this.isCreating.set(false);
        const apiError = error.error;

        const message = apiError.reqBodyErrors?.length
          ? apiError.reqBodyErrors.join('\n')
          : this.isArabic()
            ? apiError.messageAr || apiError.messageEn
            : apiError.messageEn || apiError.messageAr;

        this.createReasonError.set(message);
      },
    });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }
}
